import React from "react";
import * as G from "../Grid";
import * as M from "../../Modal";
import * as H from "../../../hooks";
import * as C from "../../../Common";
import * as S from "../../../services";
import { CellsTypes as CT } from "../AgGridDefs";
import { T, TABS, TC } from "../../../Constants";
import _ from "lodash";
import { v4 as uuid } from "uuid";

type Data = ReturnType<T.API.EDL.EQUIP_DATA.GetResource>[number] & {
    nen2767_gravity: string;
    nen2767_intensity: string;
    nen2767_omniclass: string[];
};
type Row = Data;

type NEN2767Option = T.Option<Record<"NENType", "gravity" | "intensity" | "amplitude">, string>;

const DefaultsRem: React.FC = () => {
    const lg = H.useLanguage();
    H.useAuth({ onlyAdmin: true });
    const loading = H.useBoolean(false);
    const [gammes, setGammes] = H.useAsyncState<T.Option[]>([]);
    const [data, set_data, status] = H.useAsyncState<Data[]>([]);
    const [nen2767_options, setNen2767Options] = H.useAsyncState<NEN2767Option[]>([]);

    React.useEffect(() => {
        let isSubscribed = true;
        S.getGammesOptionsLight({ is_equip: "false" })
            .then(({ data }) => isSubscribed && setGammes(data.map(d => ({ label: `${d.label} - ${d.omniclass}`, value: d.value })), "done"))
            .catch(() => isSubscribed && setGammes([], "error"));
        return () => {
            isSubscribed = false;
            setGammes([], "load");
        }
    }, [setGammes, lg]);

    React.useEffect(() => {
        let isSubscribed = true;
        S.getEquipmentResource({ type: "NEN2767", lang: "none" }, true)
            .then(({ data }) => isSubscribed && setNen2767Options(data as NEN2767Option[], "done"))
            .catch(() => isSubscribed && setNen2767Options([], "error"));
        return () => {
            isSubscribed = false;
            setNen2767Options([], "load");
        }
    }, [setNen2767Options]);

    const nen2767_gravity = React.useMemo(() => nen2767_options.filter(o => o.NENType === "gravity"), [nen2767_options]);
    const nen2767_intensity = React.useMemo(() => nen2767_options.filter(o => o.NENType === "intensity"), [nen2767_options]);

    const columns = React.useMemo<G.TableProps<Row>["columns"]>(() => [
        { field: "label", headerName: TC.NEN2767_REFERENCE },
        { field: "tr_label", headerName: TC.DESCRIPTION, editable: false },
        { field: "tr_gravity", headerName: TC.NEN2767_DEFAULT_GRAVITY, type: CT.TYPE_SELECT, params: { values: nen2767_gravity, field_value: "nen2767_gravity" } },
        { field: "tr_intensity", headerName: TC.NEN2767_DEFAULT_INTENSITY, type: CT.TYPE_SELECT, params: { values: nen2767_intensity, field_value: "nen2767_intensity" } },
        { field: "tr_omniclass", headerName: TC.NEN2767_OMNICLASS, type: CT.TYPE_SELECT, params: { values: gammes, multiple: true, auto_fit: true, typeahead: { maxResults: 100 }, field_value: "nen2767_omniclass" } },
    ], [gammes, nen2767_gravity, nen2767_intensity]);

    React.useEffect(() => {
        let isSubscribed = true;
        S.getEquipmentResource({ type: "rem_nen2767", lang: "none" })
            .then(({ data }) => isSubscribed && set_data(data as Data[], "done"))
            .catch(() => isSubscribed && set_data([], "error"));
        return () => {
            isSubscribed = false;
            set_data([], "error");
        }
    }, [set_data]);

    const rows = React.useMemo(() => data.map(opt => {
        let gravity_label = nen2767_gravity.find(g => g.value === opt.nen2767_gravity)?.label || opt.nen2767_gravity;
        let intensity_label = nen2767_intensity.find(i => i.value === opt.nen2767_intensity)?.label || opt.nen2767_intensity;
        let gamme_default_names = (opt.nen2767_omniclass || []).map(nen_gamme => gammes.find(g => g.value === nen_gamme)?.label || nen_gamme);
        let tr_omniclass = (opt.nen2767_omniclass || []).map((g, i) => lg.getTextObj(g, "name", gamme_default_names[i]));
        // Translate the label
        let tr_label = lg.getStaticText(opt.label) || opt.label;
        let tr_gravity = lg.getStaticText(gravity_label) || gravity_label;
        let tr_intensity = lg.getStaticText(intensity_label) || intensity_label;

        let row = { ...opt, tr_label, tr_gravity, tr_intensity, tr_omniclass };
        return row;

    }), [data, lg, gammes, nen2767_gravity, nen2767_intensity]);

    const events = React.useMemo(() => ({
        add_option: () => {
            // Ask user what option code he wants to add
            let params: Parameters<typeof M.renderQuickInput<Parameters<typeof S.createOption>[0][number]>>[0] = {
                modal: { isFullScreen: true, title: TC.ADD_OPTION_TITLE },
                onCheck: rows => {
                    let errors: T.Errors<typeof rows[number]>[] = [];
                    for (let row of rows) {
                        let row_errors: T.Errors<typeof row> = {};
                        // Label is required
                        if (!row.label) row_errors.label = TC.GLOBAL_REQUIRED_FIELD;
                        // Add the errors to the list
                        errors.push(row_errors);
                    }
                    return errors;
                },
                columns: [
                    { prop: "label", label: TC.NEN2767_REFERENCE, type: "text" },
                    { prop: "nen2767_gravity", label: TC.NEN2767_DEFAULT_GRAVITY, type: "number" },
                    { prop: "nen2767_intensity", label: TC.NEN2767_DEFAULT_INTENSITY, type: "number" },
                    { prop: "nen2767_omniclass", label: TC.NEN2767_OMNICLASS, type: "text" },
                ],
            }

            M.renderQuickInput(params).then(new_rows => {
                if (Array.isArray(new_rows) && new_rows.length > 0) {
                    let new_options = [];
                    let new_options_data = [];

                    new_rows.forEach(({ label, value, ...extra }) => {
                        let value_uuid = uuid();
                        let new_option = { label, value: value_uuid, option_code: "rem_nen2767" as any, extra: JSON.stringify(extra) };
                        let new_option_data = { label, value: value_uuid, ...extra };

                        new_options.push(new_option);
                        new_options_data.push(new_option_data);
                    });

                    S.createOption(new_options)
                        .then(() => set_data(p => [...p, ...new_options_data]))
                        .catch(M.Alerts.updateError);
                }
            });
        },
        remove_option: (row: Row) => {
            if (!row) return;
            else M.askConfirm().then(confirmed => {
                if (confirmed) S.removeOption({ option_code: "rem_nen2767", value: row.value.toString() })
                    .then(() => set_data(p => p.filter(opt => opt.value !== row.value)))
                    .catch(M.Alerts.deleteError);
            });
        },
    }), [set_data, data]);

    const context_menu = React.useCallback<G.TableProps<Row>["getContextMenuItems"]>(params => {
        let items = [
            {
                action: events.add_option,
                name: lg.getStaticText(TC.ADD),
                icon: "<i class='fas fa-plus'></i>",
            },
            {
                name: lg.getStaticText(TC.GLOBAL_DELETE),
                icon: "<i class='fas fa-times text-danger'></i>",
                action: () => events.remove_option(params.node.data),
            }
        ] as ReturnType<G.TableProps<Row>["getContextMenuItems"]>;

        if (items.length > 0 && params.defaultItems) items.push("separator");
        if (params.defaultItems) items.push(...params.defaultItems);
        return items;
    }, [events, lg]);

    const onValueChange = React.useCallback<G.TableProps<Row>["onValueChange"]>(event => {
        let new_value: string;
        let state_prop: keyof Row = event.colDef.field;
        let prop: keyof typeof rows[number] = event.colDef.field;
        let sql_prop: keyof Omit<T.pSQL.AppOptions, "option_code">;

        const extra = { nen2767_omniclass: event.data.nen2767_omniclass, nen2767_gravity: event.data.nen2767_gravity, nen2767_intensity: event.data.nen2767_intensity };

        if (prop === "label") {
            sql_prop = "label";
            state_prop = "label";
            new_value = event.newValue;
        }
        else if (prop === "tr_gravity") {
            sql_prop = "extra";
            state_prop = "nen2767_gravity";
            extra.nen2767_gravity = event.newValue;
        }
        else if (prop === "tr_intensity") {
            sql_prop = "extra";
            state_prop = "nen2767_intensity";
            extra.nen2767_intensity = event.newValue;
        }
        else if (prop === "tr_omniclass") {
            sql_prop = "extra";
            state_prop = "nen2767_omniclass";
            extra.nen2767_omniclass = event.newValue;
        }

        if (sql_prop === "extra") new_value = JSON.stringify(extra);

        if (sql_prop && new_value) S.updateOption({ option_code: "rem_nen2767", value: event.data.value, prop: sql_prop, new_value }).then(({ data }) => {
            set_data(p => p.map(opt => opt.value === event.data.value ? { ...opt, [state_prop]: event.newValue } : opt));
        }).catch(M.Alerts.updateError);
    }, [set_data]);

    return <div className="w-100">
        <C.Spinner error={status === "error"}>
            <G.Table<Row>
                sideBar
                rows={rows}
                status={status}
                columns={columns}
                columns_base="all"
                getRowId={r => r.data.value.toString()}
                onValueChange={onValueChange}
                getContextMenuItems={context_menu}
                adaptableId={TABS.ADMIN_OPTIONS_TABLE}
                loading={loading.value || status === "load"}
            />
        </C.Spinner>
    </div>;
}

export default DefaultsRem;